import { useQuery, useZero } from "@rocicorp/zero/react";
import { useMemo } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { personalUseBlocked } from "../../../shared/personalUse";
import { useCurrentUserGrants } from "../../hooks/useCurrentUserGrants";
import { useCurrentUserGroups } from "../../hooks/useCurrentUserGroups";
import { useRecordChatComposerControl } from "../../hooks/useRecordChatComposerControl";
import { getWritableScopeIds } from "../../lib/getWritableScopeIds";
import { ConnectorsBar } from "../ConnectorsBar";

type Props = {
  personalChatId: string;
  chatOwnerId: string;
  readOnly?: boolean;
  vertical?: boolean;
};

export function PersonalChatConnectorsBar({
  personalChatId,
  chatOwnerId,
  readOnly = false,
  vertical = false,
}: Props) {
  const z = useZero();
  const recordControl = useRecordChatComposerControl();
  const { currentUser } = useCurrentUserGroups();

  // A chat can only use connectors the current user can use, so the picker
  // offers just those.
  const [allApps] = useQuery(queries.connectorsIndex());
  const [myGrants] = useCurrentUserGrants();
  const usableConnectorIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        allApps
          .filter((app) => !personalUseBlocked(app, { kind: "personal_chat" }))
          .map((app) => app.id),
        "connected_app:use"
      ),
    [myGrants, allApps]
  );

  const [personalChat] = useQuery(
    queries.loadPersonalChat({ personalChatId, userId: chatOwnerId })
  );

  const connectorRows = useMemo(
    () => personalChat?.appUsages ?? [],
    [personalChat?.appUsages]
  );
  const connectedAppIds = useMemo(
    () => new Set(connectorRows.map((r) => r.connectedAppId)),
    [connectorRows]
  );

  const connectors = useMemo(
    () =>
      connectorRows.flatMap((row) =>
        row.connectedApp ? [row.connectedApp] : []
      ),
    [connectorRows]
  );

  const associate = (connectedAppId: string) =>
    z.mutate(
      sharedMutators.associatePersonalChatWithConnectedApp({
        personalChatId,
        connectedAppId,
      })
    );

  const dissociate = (connectedAppId: string) =>
    z.mutate(
      sharedMutators.dissociatePersonalChatFromConnectedApp({
        personalChatId,
        connectedAppId,
      })
    );

  return (
    <ConnectorsBar
      parentNoun="chat"
      canWrite={!!currentUser && !readOnly}
      connected={connectors}
      excludeIds={connectedAppIds}
      availableIds={usableConnectorIds}
      associate={associate}
      remove={dissociate}
      onManage={() => recordControl("connector_manage", "open", personalChatId)}
      menuMargin={6}
      subtleAddButton
      vertical={vertical}
      avatarSize={32}
    />
  );
}
